import React, { useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { toast } from 'react-hot-toast';
import { ChevronRightIcon } from '@heroicons/react/24/outline';
import { getCabMeetingById } from '../../../services/cabMeetingsService';
import {
  deleteCabMeetingAttendee,
  getCabMeetingAttendeeById,
  updateCabMeetingAttendee,
} from '../../../services/cabAttendeesService';
import {
  CAB_ATTENDANCE_STATUS_OPTIONS,
  getAttendanceStatusLabel,
  getAttendeeReasonLabel,
} from '../../../constants/cabAttendeeOptions';
import type { CabAttendanceStatus } from '../../../types/cabAttendee';

const CabAttendeeEdit: React.FC = () => {
  const { meetingId, attendeeId } = useParams<{
    meetingId: string;
    attendeeId: string;
  }>();
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const [attendanceStatus, setAttendanceStatus] = useState<CabAttendanceStatus | null>(null);

  const { data: meeting, isLoading: meetingLoading } = useQuery({
    queryKey: ['cab-meeting', meetingId],
    queryFn: () => getCabMeetingById(meetingId!),
    enabled: Boolean(meetingId),
  });

  const {
    data: attendee,
    isLoading: attendeeLoading,
    isError,
  } = useQuery({
    queryKey: ['cab-meeting-attendee', meetingId, attendeeId],
    queryFn: () => getCabMeetingAttendeeById(meetingId!, attendeeId!),
    enabled: Boolean(meetingId && attendeeId),
  });

  React.useEffect(() => {
    if (attendee) {
      setAttendanceStatus(attendee.attendanceStatus as CabAttendanceStatus);
    }
  }, [attendee]);

  const attendeesPath = `/admin/cab-meeting-management/cab-meetings/${meetingId}?tab=attendees`;

  const saveMutation = useMutation({
    mutationFn: (status: CabAttendanceStatus) =>
      updateCabMeetingAttendee(meetingId!, attendeeId!, { attendanceStatus: status }),
    onSuccess: async () => {
      await queryClient.refetchQueries({ queryKey: ['cab-meeting-attendees', meetingId] });
      queryClient.invalidateQueries({
        queryKey: ['cab-meeting-attendee', meetingId, attendeeId],
      });
    },
  });

  const deleteMutation = useMutation({
    mutationFn: () => deleteCabMeetingAttendee(meetingId!, attendeeId!),
    onSuccess: async () => {
      await queryClient.refetchQueries({ queryKey: ['cab-meeting-attendees', meetingId] });
      queryClient.removeQueries({
        queryKey: ['cab-meeting-attendee', meetingId, attendeeId],
      });
    },
  });

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!attendanceStatus) return;

    try {
      await saveMutation.mutateAsync(attendanceStatus);
      toast.success('Attendee updated successfully.');
      navigate(attendeesPath);
    } catch (err: unknown) {
      const message = (err as { response?: { data?: { message?: string } } })?.response?.data
        ?.message;
      toast.error(message || 'Failed to update attendee.');
    }
  };

  const handleDelete = async () => {
    if (!attendee?.canDelete) {
      toast.error('This attendee cannot be removed.');
      return;
    }
    if (!window.confirm('Remove this attendee from the meeting?')) return;

    try {
      await deleteMutation.mutateAsync();
      toast.success('Attendee removed successfully.');
      navigate(attendeesPath);
    } catch (err: unknown) {
      const message = (err as { response?: { data?: { message?: string } } })?.response?.data
        ?.message;
      toast.error(message || 'Failed to remove attendee.');
    }
  };

  if (meetingLoading || attendeeLoading || !attendee || attendanceStatus === null) {
    return <div className="p-6 text-gray-600">Loading...</div>;
  }

  if (!meeting || isError) {
    return (
      <div className="p-6">
        <h1 className="text-xl font-bold text-gray-900 mb-2">CAB Meeting Attendee</h1>
        <p className="text-gray-600 mb-4">Attendee not found.</p>
        <button
          type="button"
          onClick={() => navigate(meetingId ? attendeesPath : '/admin/cab-meeting-management/cab-definitions')}
          className="px-4 py-2 text-sm border border-gray-300 rounded-md text-gray-700 bg-white hover:bg-gray-50"
        >
          Back
        </button>
      </div>
    );
  }

  const isBusy = saveMutation.isPending || deleteMutation.isPending;

  return (
    <div className="p-3 flex flex-col h-full min-h-0 overflow-auto">
      <nav className="mb-2 flex-shrink-0 text-sm text-gray-500" aria-label="Breadcrumb">
        <ol className="flex items-center gap-1 flex-wrap">
          <li>Admin</li>
          <li>
            <ChevronRightIcon className="h-4 w-4 inline text-gray-400" aria-hidden />
          </li>
          <li>
            <Link
              to="/admin/cab-meeting-management/cab-definitions"
              className="hover:text-indigo-600"
            >
              CAB Meeting Management
            </Link>
          </li>
          <li>
            <ChevronRightIcon className="h-4 w-4 inline text-gray-400" aria-hidden />
          </li>
          <li>
            <Link
              to={`/admin/cab-meeting-management/cab-definitions/${meeting.cabDefinitionId}?tab=meetings`}
              className="hover:text-indigo-600"
            >
              {meeting.cabDefinitionName}
            </Link>
          </li>
          <li>
            <ChevronRightIcon className="h-4 w-4 inline text-gray-400" aria-hidden />
          </li>
          <li>
            <Link
              to={attendeesPath}
              className="hover:text-indigo-600 truncate max-w-xs inline-block align-bottom"
            >
              {meeting.name}
            </Link>
          </li>
          <li>
            <ChevronRightIcon className="h-4 w-4 inline text-gray-400" aria-hidden />
          </li>
          <li className="text-gray-900 font-medium">{attendee.attendeeName}</li>
        </ol>
      </nav>

      <div className="bg-white shadow rounded-lg p-4 sm:p-6">
        <div className="mb-6 pb-4 border-b border-gray-200">
          <h1 className="text-xl font-bold text-gray-900">CAB Meeting Attendee</h1>
          <p className="text-sm text-gray-600 mt-1">{attendee.attendeeName}</p>
        </div>

        <form onSubmit={handleSubmit} className="space-y-4" noValidate>
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-x-8 gap-y-4">
            <div className="grid grid-cols-12 gap-3 items-start">
              <div className="col-span-12 sm:col-span-4 lg:col-span-3 pt-2 text-right sm:pr-2">
                <label className="text-sm font-medium text-gray-700">Attendee</label>
              </div>
              <div className="col-span-12 sm:col-span-8 lg:col-span-9">
                <input
                  type="text"
                  value={attendee.attendeeName}
                  readOnly
                  className="w-full px-3 py-1.5 border border-gray-200 rounded text-sm bg-gray-50 text-gray-700"
                />
                {attendee.email && (
                  <p className="mt-1 text-xs text-gray-500">{attendee.email}</p>
                )}
              </div>
            </div>

            <div className="grid grid-cols-12 gap-3 items-start">
              <div className="col-span-12 sm:col-span-4 lg:col-span-3 pt-2 text-right sm:pr-2">
                <label className="text-sm font-medium text-gray-700">Reason</label>
              </div>
              <div className="col-span-12 sm:col-span-8 lg:col-span-9">
                <input
                  type="text"
                  value={getAttendeeReasonLabel(attendee.reason)}
                  readOnly
                  className="w-full px-3 py-1.5 border border-gray-200 rounded text-sm bg-gray-50 text-gray-700"
                />
              </div>
            </div>

            <div className="grid grid-cols-12 gap-3 items-start">
              <div className="col-span-12 sm:col-span-4 lg:col-span-3 pt-2 text-right sm:pr-2">
                <label htmlFor="attendee-attendance" className="text-sm font-medium text-gray-700">
                  Attendance
                </label>
              </div>
              <div className="col-span-12 sm:col-span-8 lg:col-span-9">
                <select
                  id="attendee-attendance"
                  value={attendanceStatus}
                  onChange={(e) => setAttendanceStatus(e.target.value as CabAttendanceStatus)}
                  className="w-full px-3 py-1.5 border border-gray-300 rounded text-sm focus:outline-none focus:ring-1 focus:ring-indigo-500"
                >
                  {CAB_ATTENDANCE_STATUS_OPTIONS.map((opt) => (
                    <option key={opt.value} value={opt.value}>
                      {opt.label}
                    </option>
                  ))}
                </select>
                <p className="mt-1 text-xs text-gray-500">
                  Current: {getAttendanceStatusLabel(attendee.attendanceStatus)}
                </p>
              </div>
            </div>

            <div className="grid grid-cols-12 gap-3 items-start">
              <div className="col-span-12 sm:col-span-4 lg:col-span-3 pt-2 text-right sm:pr-2">
                <label className="text-sm font-medium text-gray-700">Invite Sent</label>
              </div>
              <div className="col-span-12 sm:col-span-8 lg:col-span-9">
                <input
                  type="text"
                  value={attendee.inviteSent ? 'True' : 'False'}
                  readOnly
                  className="w-28 px-3 py-1.5 border border-gray-200 rounded text-sm bg-gray-50 text-gray-700"
                />
              </div>
            </div>
          </div>

          <div className="flex flex-wrap items-center gap-3 pt-4 border-t border-gray-200">
            <button
              type="submit"
              disabled={isBusy}
              className="px-4 py-2 text-sm text-white bg-indigo-600 rounded-md hover:bg-indigo-700 disabled:opacity-50"
            >
              {saveMutation.isPending ? 'Updating...' : 'Update'}
            </button>
            {attendee.canDelete && (
              <button
                type="button"
                onClick={handleDelete}
                disabled={isBusy}
                className="px-4 py-2 text-sm text-white bg-red-600 rounded-md hover:bg-red-700 disabled:opacity-50"
              >
                {deleteMutation.isPending ? 'Deleting...' : 'Delete'}
              </button>
            )}
            <button
              type="button"
              onClick={() => navigate(attendeesPath)}
              disabled={isBusy}
              className="px-4 py-2 text-sm border border-gray-300 rounded-md text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50"
            >
              Cancel
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default CabAttendeeEdit;
